import React, { useState, useRef, useEffect } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { NUMBER_PER_PAGE } from './constants';
import ProductStatusText from './ProductStatusText';
import ProductQuantity from './ProductQuantity';

const SortHeader = ({ field, label, width, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  return (
    <th
      scope="col"
      style={{
        width: width || 'auto',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: field === 'name' ? 'normal' : 'nowrap'
      }}
      onClick={() => onSort(field)}
    >
      {label}
      {active && (
        sortOrder === 'asc' ? (
          <i className="bi bi-arrow-up ml-1" style={{ fontSize: '0.875rem' }}></i>
        ) : (
          <i className="bi bi-arrow-down ml-1" style={{ fontSize: '0.875rem' }}></i>
        )
      )}
    </th>
  );
};

const ProductTable = ({
  products,
  selectedProductIds,
  toggleProductSelection,
  selectAllOnPage,
  selectAllMatchingFilters,
  isAllOnPageSelected,
  isSomeOnPageSelected,
  selectAllCheckboxRef,
  selectAllDropdownRef,
  showSelectAllDropdown,
  setShowSelectAllDropdown,
  selectAllIdsLoading,
  totalCount,
  sortBy,
  sortOrder,
  handleSort,
  handleOpenArrivalDropdown,
  showArrivalDropdown,
  productForArrival,
  arrivalDropdownPosition,
  deliveredQuantity,
  setDeliveredQuantity,
  purchasePrice,
  setPurchasePrice,
  addQuantity,
  onOpenStockHistory,
  onEditProduct,
  onDeleteProduct,
  onUpdateDiscount,
  productModalId = '#productModal',
  confirmDeleteModalId = '#confirmDeleteModal',
  pageCount,
  currentPage,
  onPageChange
}) => {
  const [editingDiscountProductId, setEditingDiscountProductId] = useState(null);
  const [editingDiscountValue, setEditingDiscountValue] = useState('');
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const discountInputRef = useRef(null);

  useEffect(() => {
    if (editingDiscountProductId != null && discountInputRef.current) {
      discountInputRef.current.focus();
      discountInputRef.current.select();
    }
  }, [editingDiscountProductId]);

  const startEditingDiscount = (product) => {
    setEditingDiscountProductId(product.id);
    setEditingDiscountValue(product.discount != null ? String(product.discount) : '');
  };

  const saveDiscount = (productId) => {
    const val = editingDiscountValue.trim();
    const num = val === '' ? 0 : parseInt(val, 10);
    setEditingDiscountProductId(null);
    setEditingDiscountValue('');
    if (isNaN(num) || num < 0 || num > 100) return;
    if (onUpdateDiscount) onUpdateDiscount(productId, num);
  };

  const cancelEditingDiscount = () => {
    setEditingDiscountProductId(null);
    setEditingDiscountValue('');
  };

  const handleDiscountKeyDown = (e, productId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveDiscount(productId);
    }
    if (e.key === 'Escape') {
      cancelEditingDiscount();
    }
  };

  const handleDiscountBlur = (productId) => {
    if (editingDiscountProductId === productId) {
      saveDiscount(productId);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col p-0">
          <table className="table ap-products-table" style={{ borderTop: 'none', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th
                  scope="col"
                  style={{ width: '110px', minWidth: '85px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', verticalAlign: 'middle', paddingLeft: '4px', paddingRight: '4px' }}
                  onClick={() => handleSort('id')}
                >
                  <div
                    ref={selectAllDropdownRef}
                    className="d-inline-flex align-items-center position-relative"
                    style={{ marginRight: '6px', marginTop: '3px', gap: '2px' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <label
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0"
                      style={{ width: '28px', height: '28px', cursor: 'pointer' }}
                      title="Вибрати всі на сторінці"
                    >
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        className="m-0"
                        checked={isAllOnPageSelected}
                        onChange={selectAllOnPage}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '1rem', height: '1rem', cursor: 'pointer', position: 'static', margin: 0 }}
                      />
                    </label>
                    {totalCount > NUMBER_PER_PAGE && (
                      <>
                        <button
                          type="button"
                          className="d-inline-flex align-items-center justify-content-center border rounded flex-shrink-0 bg-white p-0"
                          style={{ width: '18px', height: '18px', cursor: 'pointer', borderColor: '#dee2e6' }}
                          onClick={() => setShowSelectAllDropdown(!showSelectAllDropdown)}
                          title="Вибір: на сторінці або весь список"
                          aria-expanded={showSelectAllDropdown}
                        >
                          <i className="bi bi-chevron-down" style={{ fontSize: '0.6rem' }} />
                        </button>
                        {showSelectAllDropdown && (
                          <>
                            <div
                              className="position-fixed"
                              style={{ inset: 0, zIndex: 1040 }}
                              onClick={() => setShowSelectAllDropdown(false)}
                              aria-hidden="true"
                            />
                            <div className="dropdown-menu show position-absolute mt-1" style={{ zIndex: 1050, left: 0 }}>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => { selectAllOnPage(); setShowSelectAllDropdown(false); }}
                              >
                                Вибрати все на сторінці
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={selectAllMatchingFilters}
                                disabled={selectAllIdsLoading}
                              >
                                {selectAllIdsLoading ? 'Завантаження…' : 'Вибрати весь список'}
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <span className="ml-1">Id</span>
                  {sortBy === 'id' && (
                    sortOrder === 'asc' ? <i className="bi bi-arrow-up ml-1" style={{ fontSize: '0.875rem' }} /> : <i className="bi bi-arrow-down ml-1" style={{ fontSize: '0.875rem' }} />
                  )}
                </th>
                <SortHeader field="name" label="Назва" width="auto" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <th scope="col" style={{ width: '280px', whiteSpace: 'nowrap' }}>Статус</th>
                <SortHeader field="price" label="Ціна" width="80px" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortHeader field="quantity" label="Кількість" width="110px" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortHeader field="discount" label="Знижка" width="96px" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <th scope="col" style={{ width: '160px', whiteSpace: 'nowrap' }}>Дії</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id} data-admin-product-id={product.id}>
                  <th scope="row" style={{ verticalAlign: 'middle', paddingLeft: '4px', paddingRight: '4px' }}>
                    <div className="d-flex align-items-center flex-nowrap" style={{ gap: '8px' }}>
                      <label
                        className="d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0"
                        style={{ width: '28px', height: '28px', cursor: 'pointer' }}
                        title={`Обрати товар ${product.id}`}
                      >
                        <input
                          type="checkbox"
                          className="m-0"
                          checked={selectedProductIds.includes(product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                          onClick={e => e.stopPropagation()}
                          style={{ width: '1rem', height: '1rem', margin: 0, cursor: 'pointer', position: 'static' }}
                        />
                      </label>
                      <span className="flex-shrink-0">{product.id}</span>
                    </div>
                  </th>
                  <td
                    onMouseEnter={() => setHoveredProductId(product.id)}
                    onMouseLeave={() => setHoveredProductId(null)}
                  >
                    {product.name}
                    <span className="d-md-none" style={{ marginLeft: '1px' }}>
                      <Link to={'/product/' + product.id} style={{ color: 'black' }} title="Перейти на сторінку товару">
                        <i className='bi bi-forward-fill'></i>
                      </Link>
                    </span>
                    {hoveredProductId === product.id && (
                      <span className="d-none d-md-inline" style={{ marginLeft: '1px' }}>
                        <Link to={'/product/' + product.id} style={{ color: 'black' }} title="Перейти на сторінку товару">
                          <i className='bi bi-forward-fill'></i>
                        </Link>
                      </span>
                    )}
                  </td>
                  <td>
                    <ProductStatusText state={product.state} />
                  </td>
                  <td className="ap-number-cell">{product.price}</td>
                  <td className="ap-number-cell"><ProductQuantity product={product} /></td>
                  <td
                    onClick={() => editingDiscountProductId !== product.id && startEditingDiscount(product)}
                    style={{ cursor: editingDiscountProductId !== product.id ? 'pointer' : 'default', minWidth: '50px' }}
                  >
                    {editingDiscountProductId === product.id ? (
                      <input
                        ref={discountInputRef}
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: '60px', display: 'inline-block' }}
                        value={editingDiscountValue}
                        onChange={e => setEditingDiscountValue(e.target.value)}
                        onBlur={() => handleDiscountBlur(product.id)}
                        onKeyDown={e => { handleDiscountKeyDown(e, product.id); e.stopPropagation(); }}
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span>{product.discount != null ? `${product.discount}%` : '—'}</span>
                    )}
                  </td>
                  <td className="ap-action-cell">
                    <div className="position-relative arrival-dropdown-container" style={{ display: 'inline-block' }}>
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={(e) => handleOpenArrivalDropdown(product, e)}
                        title="Внести надходження"
                        style={{ padding: '2px 4px', color: '#000' }}
                      >
                        <i className="bi bi-box-seam" style={{ fontSize: '1.125rem' }}></i>
                      </button>
                      {showArrivalDropdown && productForArrival?.id === product.id && (
                        <div
                          className="position-fixed bg-white border rounded shadow-lg p-3"
                          style={{
                            zIndex: 9999,
                            width: '320px',
                            top: arrivalDropdownPosition?.top ? `${arrivalDropdownPosition.top}px` : 'auto',
                            right: arrivalDropdownPosition?.right ? `${arrivalDropdownPosition.right}px` : 'auto',
                          }}
                        >
                          <div className="mb-2">
                            <label className="font-weight-bold">Кількість</label>
                            <input
                              type="text"
                              className="form-control"
                              value={deliveredQuantity}
                              onChange={e => setDeliveredQuantity(e.target.value)}
                              placeholder="Кількість в поставці"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="font-weight-bold">Ціна в надходженні</label>
                            <input
                              type="text"
                              className="form-control"
                              value={purchasePrice}
                              onChange={e => setPurchasePrice(e.target.value)}
                              placeholder="Ціна в надходженні"
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-dark btn-sm btn-block"
                            onClick={e => addQuantity(product.id, deliveredQuantity, purchasePrice, e)}
                          >
                            Внести та підтвердити
                          </button>
                        </div>
                      )}
                    </div>
                    {typeof onOpenStockHistory === 'function' && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => onOpenStockHistory(product)}
                        title="Історія залишків"
                        style={{ padding: '2px 4px', color: '#6c757d' }}
                      >
                        <i className="bi bi-clock-history" style={{ fontSize: '1.125rem' }}></i>
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      data-toggle="modal"
                      data-target={productModalId}
                      data-backdrop="static"
                      data-keyboard="false"
                      onClick={() => onEditProduct(product)}
                      title="Редагувати"
                      style={{ padding: '2px 4px', color: '#007bff' }}
                    >
                      <i className="bi bi-pencil-fill text-dark" style={{ fontSize: '1.125rem' }}></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      data-toggle="modal"
                      data-target={confirmDeleteModalId}
                      onClick={() => onDeleteProduct(product)}
                      title="Видалити"
                      style={{ padding: '2px 4px', color: '#dc3545' }}
                    >
                      <i className="bi bi-trash" style={{ fontSize: '1.125rem' }}></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pageCount > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <ReactPaginate
            previousLabel={'<'}
            nextLabel={'>'}
            breakLabel={'...'}
            breakClassName={'break-me'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={onPageChange}
            containerClassName={'pagination'}
            subContainerClassName={'pages pagination'}
            activeClassName={'active'}
            forcePage={currentPage}
          />
        </div>
      )}
    </>
  );
};

export default ProductTable;
